// src/components/Performance.tsx (Enhanced with expand functionality)
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Maximize2, X } from 'lucide-react';
import pipelineImage from '../assets/malwarecnnpipeline.png';
import './Performance.css';

const Performance: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [isFullscreen, setIsFullscreen] = useState(false);

  const openFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = 'unset';
  };

  const stats = [
    { label: 'Validation Accuracy', value: '98.55%', color: '#22c55e' },
    { label: 'Weighted F1', value: '0.980', color: '#6366f1' },
    { label: 'Families Detected', value: '25', color: '#f97316' },
    { label: 'Training Time', value: '~4 min', color: '#eab308' },
  ];

  return (
    <>
      <section className="performance">
        <div className="performance-container">
          <h2 className="section-title">Model Performance</h2>
          <div className="performance-grid" ref={ref}>
            <div className="stats-grid">
              {stats.map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  className="stat-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                >
                  <div className="stat-value" style={{ color: stat.color }}>
                    {stat.value}
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              className="matrix-container"
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="pipeline-image-container" onClick={openFullscreen}>
                <img 
                  src={pipelineImage} 
                  alt="Malware CNN Pipeline - From binary to classification" 
                  className="pipeline-image"
                />
                <div className="pipeline-overlay">
                  <Maximize2 size={24} className="expand-icon" />
                  <span>Click to expand</span>
                </div>
              </div>
              <p className="matrix-caption">
                <strong>End-to-end pipeline:</strong> Raw binary → Grayscale image → 4-block CNN feature extraction → Classifier → Malware family verdict.
                Each block learns progressively complex patterns: from low-level byte textures to abstract threat representations.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            className="fullscreen-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFullscreen}
          >
            <motion.div
              className="fullscreen-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-fullscreen" onClick={closeFullscreen}>
                <X size={32} />
              </button>
              <img
                src={pipelineImage}
                alt="Malware CNN Pipeline - Full size"
                className="fullscreen-image"
              />
              <p className="fullscreen-caption">
                <strong>Malware CNN Pipeline:</strong> Binary → Image → 4-block CNN → Classification
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Performance;